import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExpandedViewComponent } from '@components';
import {
  ChartSize,
  Kpi,
  KpiBarChartData,
  KpiChartConfig,
  KpiChartData,
  KpiChartId,
  KpiGroupedBarChartData,
  KpiKeys,
  KpiNames,
  KpiPieChartData,
  KpiSupportedToggleChartData,
  KpiToggleChartData
} from '@models';
import { ExpandedPanelStateService } from '@services';
import {
  BAR_CHART_OPTION,
  GROUPED_BAR_CHART_OPTION,
  PIE_CHART_COLOR_MAPPING,
  PIE_CHART_OPTION
} from 'app/catalogues/chart.catalogue';
import { KpiChartFacadeService } from 'app/services/kpi-chart-facade/kpi-chart-facade.service';
import { KpiChartFilterService } from 'app/services/kpi-chart-filter/kpi-chart-filter.service';
import { EChartsOption } from 'echarts';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil,
  withLatestFrom
} from 'rxjs';

@Component({
  selector: 'kpi-charts-expanded-panel',
  templateUrl: './kpi-charts-expanded-panel.component.html',
  styleUrl: './kpi-charts-expanded-panel.component.scss'
})
export class KpiChartsExpandedPanelComponent implements OnInit, OnDestroy {
  private static readonly ROW_THRESHOLD = 5;
  private static readonly LARGE_TABLE_ROWSPAN = 2;
  private static readonly DEFAULT_TABLE_ROWSPAN = 1;
  private static readonly DEFAULT_ROW_HEIGHT = 330;
  private static readonly MAX_ROW_HEIGHT = 674;
  private static readonly FIRST_INCREMENT = 16;
  private static readonly HEIGHT_INCREMENT_PER_ROW = 40;
  private expandedPanelStateService: ExpandedPanelStateService = inject(ExpandedPanelStateService);
  private kpiChartFacade: KpiChartFacadeService = inject(KpiChartFacadeService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  kpiChartFilterService = inject(KpiChartFilterService);

  private destroy$ = new Subject<void>();
  private loadingState$ = new BehaviorSubject<boolean>(false);

  @Input() selectedKpiByKey!: { key: KpiKeys; kpi: Kpi };
  @Input() protocolNumber!: string;
  @Input() kpiChartsConfig!: KpiChartConfig[];

  @Output() closeButtonClicked = new EventEmitter<void>();

  kpiChartData$!: Observable<KpiChartData | null>;
  displayValues$!: Observable<{
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  }>;

  bar: EChartsOption = BAR_CHART_OPTION;
  pie: EChartsOption = PIE_CHART_OPTION;
  groupedBar: EChartsOption = GROUPED_BAR_CHART_OPTION;
  colorMap = PIE_CHART_COLOR_MAPPING;
  chartSize: ChartSize = 'medium';

  isLoadingState = false;

  private currentKpiChartData: KpiChartData | null = null;
  private currentDisplayValues: {
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  } = {};

  reactiveTrendChartData$!: Observable<any>;
  tableRowspan$!: Observable<number>;
  dynamicRowHeight$!: Observable<string>;

  ngOnInit(): void {
    this.kpiChartData$ = this.kpiChartFacade.activeKpiChartData$;
    this.displayValues$ = this.kpiChartFacade.displayValues$;

    this.tableRowspan$ = combineLatest([
      this.kpiChartData$,
      this.kpiChartFacade.isActiveKpiLoading$.pipe(startWith(true))
    ]).pipe(
      map(([chartData, isLoading]) => {
        if (isLoading || !this.kpiChartFacade.hasValidChartData(chartData)) {
          return KpiChartsExpandedPanelComponent.DEFAULT_TABLE_ROWSPAN;
        }

        const rowCount = chartData?.charts.keyMilestonesTable?.rows?.length || 0;
        return rowCount > KpiChartsExpandedPanelComponent.ROW_THRESHOLD
          ? KpiChartsExpandedPanelComponent.LARGE_TABLE_ROWSPAN
          : KpiChartsExpandedPanelComponent.DEFAULT_TABLE_ROWSPAN;
      }),
      distinctUntilChanged()
    );

    this.dynamicRowHeight$ = combineLatest([
      this.kpiChartData$,
      this.kpiChartFacade.isActiveKpiLoading$.pipe(startWith(true))
    ]).pipe(
      map(([chartData, isLoading]) => {
        if (isLoading || !this.kpiChartFacade.hasValidChartData(chartData)) {
          return `${KpiChartsExpandedPanelComponent.DEFAULT_ROW_HEIGHT}px`;
        }

        const rowCount = chartData?.charts.keyMilestonesTable?.rows?.length || 0;

        if (rowCount > KpiChartsExpandedPanelComponent.ROW_THRESHOLD) {
          const extraRows = rowCount - KpiChartsExpandedPanelComponent.ROW_THRESHOLD;
          const calculatedHeight =
            KpiChartsExpandedPanelComponent.DEFAULT_ROW_HEIGHT +
            KpiChartsExpandedPanelComponent.FIRST_INCREMENT +
            Math.max(0, extraRows - 1) * KpiChartsExpandedPanelComponent.HEIGHT_INCREMENT_PER_ROW;

          const finalHeight = Math.min(
            calculatedHeight,
            KpiChartsExpandedPanelComponent.MAX_ROW_HEIGHT
          );
          return `${finalHeight}px`;
        }

        return `${KpiChartsExpandedPanelComponent.DEFAULT_ROW_HEIGHT}px`;
      }),
      distinctUntilChanged()
    );

    this.reactiveTrendChartData$ = combineLatest([
      this.kpiChartData$,
      this.kpiChartFacade.toggleStates$
    ]).pipe(
      map(([chartData, toggleStates]) => {
        if (!chartData?.charts?.trendchart || !this.selectedKpiByKey) {
          return { data: null, trendToggleState: null };
        }

        const trendData = chartData.charts.trendchart;
        const kpiKey = this.selectedKpiByKey.key;
        const kpiToggleStates = toggleStates[kpiKey];

        const trendToggleState = trendData.hasToggle
          ? kpiToggleStates
            ? kpiToggleStates[trendData.identifier as KpiChartId]
            : null
          : null;

        return {
          data: trendData,
          trendToggleState,
          chartId: trendData.identifier
        };
      }),

      distinctUntilChanged((prev, curr) => {
        if (!prev || !curr) return false;

        const dataEqual =
          prev.data?.identifier === curr.data?.identifier &&
          prev.data?.seriesName === curr.data?.seriesName &&
          prev.data?.series?.length === curr.data?.series?.length &&
          prev.data?.isCount === curr.data?.isCount;
        const toggleStateEqual = prev.trendToggleState === curr.trendToggleState;

        return dataEqual && toggleStateEqual;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      map(({ data, trendToggleState: _ }) => {
        if (!data) return null;

        const trendData = JSON.parse(JSON.stringify(data));

        if (trendData.hasToggle && this.selectedKpiByKey) {
          this.kpiChartFacade.applyTrendChartToggleState(this.selectedKpiByKey.key, trendData);
        }

        return trendData;
      })
    );

    this.kpiChartFacade.isActiveKpiLoading$
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.kpiChartData$.pipe(startWith(null))),
        map(([isLoading, chartData]) => {
          if (isLoading) return true;

          return !this.kpiChartFacade.hasValidChartData(chartData);
        }),
        distinctUntilChanged()
      )
      .subscribe((loadingValue) => {
        this.loadingState$.next(loadingValue);
        this.isLoadingState = loadingValue;
      });

    this.kpiChartData$.pipe(takeUntil(this.destroy$)).subscribe((chartData) => {
      this.currentKpiChartData = chartData;

      if (chartData) {
        this.syncToggleStatesWithChartData();
      }
    });

    this.displayValues$.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.currentDisplayValues = values;
    });

    this.kpiChartFacade.toggleStates$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.currentKpiChartData && this.selectedKpiByKey) {
        this.syncToggleStatesWithChartData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadingState$.complete();
  }

  get kpiChartData(): KpiChartData | null {
    return this.currentKpiChartData;
  }

  get kpiDisplayValue(): string | number | undefined {
    return this.currentDisplayValues.kpiDisplayValue;
  }

  get kpiDisplaySecondaryValue(): string | undefined {
    return this.currentDisplayValues.kpiDisplaySecondaryValue;
  }

  get kpiDisplayUnit(): string | undefined {
    return this.currentDisplayValues.kpiDisplayUnit;
  }

  onExpandedPanelCollapse(): void {
    this.expandedPanelStateService.setExpandedPanelExpanded(false);
    this.resetChartsToDefaults();
  }

  onExpandedPanelClose(): void {
    this.expandedPanelStateService.setExpandedPanelExpanded(false);
    this.resetChartsToDefaults();
    this.closeButtonClicked.emit();
  }

  openExpandedView(chartConfig: KpiChartConfig) {
    const dialogRef = this.dialog.open(ExpandedViewComponent, {
      data: {
        selectedKpi: this.selectedKpiByKey.kpi,
        chartId: chartConfig.id,
        kpiKey: this.selectedKpiByKey.key,
        ...this.getExpandedViewData(chartConfig)
      },
      panelClass: 'fullscreen-dialog',
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh'
    });

    dialogRef.afterClosed().subscribe(() => {
      if (chartConfig.type === 'toggleChart') {
        this.syncToggleStatesWithChartData();
        this.cdr.detectChanges();
      }
    });
  }

  getBarChartData(chartId: KpiChartId): KpiBarChartData | undefined {
    return this.kpiChartData?.charts.bar?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  getPieChartData(chartId: KpiChartId): KpiPieChartData | undefined {
    return this.kpiChartData?.charts.pie?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  getGroupedBarChartData(chartId: KpiChartId): KpiGroupedBarChartData | undefined {
    return this.kpiChartData?.charts.groupedBar?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  isBarChart(data: KpiSupportedToggleChartData): data is KpiBarChartData {
    return data.type === 'bar';
  }

  isPieChart(data: KpiSupportedToggleChartData): data is KpiPieChartData {
    return data.type === 'pie';
  }

  getToggleChartData(chartId: KpiChartId): KpiToggleChartData | undefined {
    const originalToggleChartData = this.kpiChartData?.charts.toggleChart?.find(
      (chartData: any) => chartData.identifier === chartId
    );

    if (!originalToggleChartData) return undefined;

    const toggleChartData = JSON.parse(JSON.stringify(originalToggleChartData));

    if (this.selectedKpiByKey) {
      const savedState = this.kpiChartFacade.getToggleState(
        this.selectedKpiByKey.key,
        toggleChartData.identifier
      );

      if (savedState) {
        toggleChartData.charts.primaryChart.selected =
          savedState === toggleChartData.charts.primaryChart.id;
        toggleChartData.charts.secondaryChart.selected =
          savedState === toggleChartData.charts.secondaryChart.id;
      } else {
        toggleChartData.charts.primaryChart.selected = true;
        toggleChartData.charts.secondaryChart.selected = false;
      }
    }

    return toggleChartData;
  }

  private syncToggleStatesWithChartData(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    let hasChanges = false;

    if (this.currentKpiChartData.charts?.trendchart?.hasToggle) {
      hasChanges = this.syncTrendChartToggleState() || hasChanges;
    }

    if (this.currentKpiChartData.charts?.toggleChart) {
      hasChanges = this.syncToggleChartStates() || hasChanges;
    }

    if (hasChanges) {
      this.cdr.detectChanges();
      this.cdr.markForCheck();
    }

    this.cdr.detectChanges();
  }

  private syncTrendChartToggleState(): boolean {
    const trendChart = this.currentKpiChartData?.charts?.trendchart;
    if (!trendChart?.hasToggle || !this.selectedKpiByKey) {
      return false;
    }

    return this.kpiChartFacade.applyTrendChartToggleState(this.selectedKpiByKey.key, trendChart);
  }

  private syncToggleChartStates(): boolean {
    const toggleCharts = this.currentKpiChartData?.charts?.toggleChart;
    if (!toggleCharts || !this.selectedKpiByKey) {
      return false;
    }

    const kpiKey = this.selectedKpiByKey.key;
    let hasChanges = false;

    toggleCharts.forEach((toggleChart) => {
      const savedState = this.kpiChartFacade.getToggleState(kpiKey, toggleChart.identifier);

      if (savedState) {
        const wasSelected = toggleChart.charts.primaryChart.selected;

        toggleChart.charts.primaryChart.selected =
          savedState === toggleChart.charts.primaryChart.id;
        toggleChart.charts.secondaryChart.selected =
          savedState === toggleChart.charts.secondaryChart.id;

        if (wasSelected !== toggleChart.charts.primaryChart.selected) {
          hasChanges = true;
        }
      }
    });

    return hasChanges;
  }

  private getExpandedViewData(chartConfig: KpiChartConfig) {
    switch (chartConfig.type) {
      case 'trendchart':
        return this.getTrendChartExpandedData();
      case 'bar':
        return this.getBarChartExpandedData(chartConfig.id);
      case 'pie':
        return this.getPieChartExpandedData(chartConfig.id);
      case 'keyMilestonesTable':
        return this.getTableExpandedData();
      case 'toggleChart':
        return this.getToggleChartExpandedData(chartConfig.id);
      case 'groupedBar':
        return this.getGroupedBarChartExpandedData(chartConfig.id);
    }
  }

  private getTrendChartExpandedData() {
    const trendChart = this.kpiChartData?.charts.trendchart;
    if (!trendChart) return;
    return {
      viewTitle: this.formatChartTitle(trendChart.titleSuffix),
      chartTitle: 'TrendLine',
      chartType: 'trend',
      expandedTrendChartData: trendChart,
      viewDescription: trendChart.tooltipText
    };
  }

  private getBarChartExpandedData(chartId: KpiChartId) {
    const barChartData = this.getBarChartData(chartId);
    if (!barChartData) return;
    return {
      viewTitle: this.formatChartTitle(barChartData.titleSuffix),
      chartTitle: 'BarChart',
      chartType: 'bar',
      expandedBarChartData: barChartData,
      viewDescription: barChartData.tooltipText,
      fieldFilter: barChartData.fieldFilter,
      filterService: this.kpiChartFilterService
    };
  }

  private getPieChartExpandedData(chartId: KpiChartId) {
    const pieChartData = this.getPieChartData(chartId);
    if (!pieChartData) return;
    return {
      viewTitle: this.formatChartTitle(pieChartData.titleSuffix),
      chartTitle: 'PieChart',
      chartType: 'pie',
      expandedPieChartData: pieChartData,
      viewDescription: pieChartData.tooltipText,
      fieldFilter: pieChartData.fieldFilter,
      filterService: this.kpiChartFilterService
    };
  }

  private getGroupedBarChartExpandedData(chartId: KpiChartId) {
    const groupedBarChartData = this.getGroupedBarChartData(chartId);
    if (!groupedBarChartData) return;
    return {
      viewTitle: this.formatChartTitle(groupedBarChartData.titleSuffix),
      chartTitle: 'GroupedBarChart',
      chartType: 'groupedBar',
      expandedBarChartData: groupedBarChartData,
      viewDescription: groupedBarChartData.tooltipText,
      fieldFilter: groupedBarChartData.fieldFilter,
      filterService: this.kpiChartFilterService
    };
  }

  private getTableExpandedData() {
    const tableData = this.kpiChartData?.charts.keyMilestonesTable;
    if (!tableData) return;
    return {
      viewTitle: tableData.title,
      chartTitle: 'KeyMilestonesTable',
      chartType: 'table',
      tableData: tableData
    };
  }

  private getToggleChartExpandedData(chartId: KpiChartId) {
    const toggleChartData = this.getToggleChartData(chartId);
    if (!toggleChartData) return;
    return {
      viewTitle: this.formatChartTitle(toggleChartData.titleSuffix),
      chartTitle: 'ToggleChart',
      chartType: 'toggle',
      toggleChartData: toggleChartData,
      viewDescription: toggleChartData.charts.primaryChart.selected
        ? toggleChartData.charts.primaryChart.data.tooltipText
        : toggleChartData.charts.secondaryChart.data.tooltipText,
      filterService: this.kpiChartFilterService
    };
  }

  private createReactiveChartStream<T>(
    chartIdentifier: KpiChartId,
    chartDataSelector: (chartData: KpiChartData) => T | null
  ): Observable<T | null> {
    return combineLatest([this.kpiChartData$, this.kpiChartFacade.toggleStates$]).pipe(
      map(([chartData, toggleStates]) => {
        if (!chartData || !this.selectedKpiByKey) {
          return { data: null, toggleState: null, chartId: chartIdentifier };
        }

        const selectedChartData = chartDataSelector(chartData);
        const kpiKey = this.selectedKpiByKey.key;
        const kpiToggleStates = (toggleStates[kpiKey] as Record<KpiChartId, KpiChartId>) || {};
        const toggleState = kpiToggleStates[chartIdentifier];

        return {
          data: selectedChartData,
          toggleState,
          chartId: chartIdentifier
        };
      }),
      distinctUntilChanged((prev, curr) => {
        if (!prev || !curr) return false;

        const dataEqual = prev.data === curr.data;
        const toggleStateEqual = prev.toggleState === curr.toggleState;
        return dataEqual && toggleStateEqual;
      }),
      map(({ data }) => data)
    );
  }

  getReactiveToggleChartData(chartId: KpiChartId): Observable<KpiToggleChartData | null> {
    return this.createReactiveChartStream(chartId, (chartData) => {
      const toggleChart = chartData.charts?.toggleChart?.find(
        (chart: any) => chart.identifier === chartId
      );

      if (!toggleChart) return null;

      const reactiveToggleChart = {
        ...toggleChart,
        charts: {
          ...toggleChart.charts,
          primaryChart: { ...toggleChart.charts.primaryChart },
          secondaryChart: { ...toggleChart.charts.secondaryChart }
        }
      };

      if (this.selectedKpiByKey) {
        const savedState = this.kpiChartFacade.getToggleState(this.selectedKpiByKey.key, chartId);

        if (savedState) {
          reactiveToggleChart.charts.primaryChart.selected =
            savedState === reactiveToggleChart.charts.primaryChart.id;
          reactiveToggleChart.charts.secondaryChart.selected =
            savedState === reactiveToggleChart.charts.secondaryChart.id;
        } else {
          reactiveToggleChart.charts.primaryChart.selected = true;
          reactiveToggleChart.charts.secondaryChart.selected = false;
        }
      }

      reactiveToggleChart.tooltipText = reactiveToggleChart.charts.primaryChart.selected
        ? reactiveToggleChart.charts.primaryChart.data.tooltipText
        : reactiveToggleChart.charts.secondaryChart.data.tooltipText;

      return reactiveToggleChart;
    });
  }

  getReactiveBarChartData(chartId: KpiChartId): Observable<KpiBarChartData | null> {
    return this.createReactiveChartStream(chartId, (chartData) => {
      return chartData.charts?.bar?.find((chart: any) => chart.identifier === chartId) || null;
    });
  }

  getReactivePieChartData(chartId: KpiChartId): Observable<KpiPieChartData | null> {
    return this.createReactiveChartStream(chartId, (chartData) => {
      return chartData.charts?.pie?.find((chart: any) => chart.identifier === chartId) || null;
    });
  }

  formatChartTitle(titleSuffix: string): string {
    const kpiName: KpiNames = this.selectedKpiByKey.kpi.name;
    let suffix = '';
    if (titleSuffix === 'Days in stage') {
      suffix += 'by ';
    }

    suffix += titleSuffix.toLowerCase();
    let chartTitle = `${kpiName} ${suffix}`;
    if (this.selectedKpiByKey.kpi.name === 'CRF review completed') {
      chartTitle = 'CRF review completion ' + suffix;
    }

    if (this.selectedKpiByKey.kpi.name === 'Source data verification compliance') {
      chartTitle = 'Source data verification (SDV) compliance ' + suffix;
    }

    const reasonTitleMap: Record<string, string> = {
      'Subject screen fail rate': 'Reason for screen failure',
      'Subject early termination rate': 'Reason for early termination',
      'Deviation rate per subject': 'Reason for deviation'
    };

    if (titleSuffix === 'Reason' && reasonTitleMap[kpiName]) {
      chartTitle = reasonTitleMap[kpiName];
    }
    return chartTitle;
  }

  private resetChartsToDefaults(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    this.kpiChartFacade.resetToggleStatesForKpi(
      this.selectedKpiByKey.key,
      this.currentKpiChartData
    );
    this.syncToggleStatesWithChartData();
    this.cdr.detectChanges();
  }

  public resetAllChartsToDefaults(): void {
    this.resetChartsToDefaults();
  }

  public resetToggleChartsToDefaults(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    if (this.currentKpiChartData.charts?.toggleChart) {
      this.currentKpiChartData.charts.toggleChart.forEach((toggleChart) => {
        this.kpiChartFacade.setToggleState(
          this.selectedKpiByKey.key,
          toggleChart.identifier,
          toggleChart.charts.primaryChart.id
        );
      });
    }
    this.syncToggleStatesWithChartData();
    this.cdr.detectChanges();
  }

  public resetTrendChartToDefault(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    if (this.currentKpiChartData.charts?.trendchart?.hasToggle) {
      const trendChart = this.currentKpiChartData.charts.trendchart;
      const defaultState = trendChart.identifier + '_percent';
      this.kpiChartFacade.setToggleState(
        this.selectedKpiByKey.key,
        trendChart.identifier,
        defaultState as KpiChartId
      );
    }
    this.syncToggleStatesWithChartData();
    this.cdr.detectChanges();
  }
}
